import { useEffect, useState } from "react"
import { useAtomValue } from "jotai"
import clsx from "clsx"
import { Icon } from "@/components/icons/Icon"
import { isPhotoFullscreenAtom } from "@/store/ui-views-state"
import { dialogsAtom } from "@/store/dialogs-state"
import {
	dismissMaintenance,
	fetchUpcomingMaintenance,
	formatMaintenanceWindow,
	pruneDismissedMaintenanceIds,
	STATUSPAGE_URL,
	type UpcomingMaintenance,
} from "@/utils/upcoming-maintenance"

import { useTranslation } from "react-i18next"
export const UpcomingMaintenanceNotification = () => {
	const { t } = useTranslation("components")
	const isPhotoFullscreen = useAtomValue(isPhotoFullscreenAtom)
	const hasOpenDialog = useAtomValue(dialogsAtom).length > 0
	const [maintenance, setMaintenance] = useState<UpcomingMaintenance | null>(null)

	useEffect(() => {
		const controller = new AbortController()

		const updateMaintenanceNotification = () => {
			void fetchUpcomingMaintenance(controller.signal).then((result) => {
				if (!result || controller.signal.aborted) {
					return
				}

				const dismissedIds = pruneDismissedMaintenanceIds(result.maintenanceIds)
				const startsWithin24Hours =
					result.maintenance &&
					Date.parse(result.maintenance.scheduledFor) - Date.now() <= 24 * 60 * 60 * 1000

				if (
					result.maintenance &&
					startsWithin24Hours &&
					!dismissedIds.has(result.maintenance.id)
				) {
					setMaintenance(result.maintenance)
				} else {
					setMaintenance(null)
				}
			})
		}

		updateMaintenanceNotification()
		const pollingIntervalId = window.setInterval(updateMaintenanceNotification, 60 * 1000)

		return () => {
			window.clearInterval(pollingIntervalId)
			controller.abort()
		}
	}, [])

	if (!maintenance || isPhotoFullscreen) {
		return null
	}

	const dismiss = () => {
		dismissMaintenance(maintenance.id)
		setMaintenance(null)
	}

	return (
		<section
			aria-label={t("upcomingMaintenanceNotification.upcomingScheduledMaintenance")}
			aria-live="polite"
			className={clsx(
				"fixed bottom-[60px] right-[60px] z-40 flex h-[115px] w-[320px] rounded-[3px] bg-[#3498db] text-[13.8px] tracking-[0.575px] text-white shadow-[0_20px_32px_-8px_rgba(9,20,66,0.25)] [-moz-osx-font-smoothing:auto] [-webkit-font-smoothing:auto] [font-family:-apple-system,BlinkMacSystemFont,sans-serif] [text-rendering:auto]",
				hasOpenDialog && "brightness-50",
			)}
			role="status"
		>
			<div className="flex-[1] pt-[16px] text-center">
				<Icon aria-hidden="true" className="mx-auto" name="info" size={20} />
			</div>
			<div className="flex min-w-0 flex-[6] flex-col pb-[21px] pl-[6px] pt-[16px]">
				<h2 className="line-clamp-1 min-h-[18px] w-[230px] font-medium leading-[17px]">
					{maintenance.name}
				</h2>
				<p className="mb-auto mt-[7px] flex-1 text-[11.5px] leading-[normal]">
					{formatMaintenanceWindow(maintenance)}
				</p>
				<div>
					<a
						className="leading-[normal] underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
						href={STATUSPAGE_URL}
						rel="noopener noreferrer"
						target="_blank"
					>
						{t("upcomingMaintenanceNotification.viewLatestUpdates")}
					</a>
				</div>
			</div>
			<div className="min-w-[13px] flex-[0.7] pr-[16px] pt-[16px] text-right">
				<button
					aria-label={t(
						"upcomingMaintenanceNotification.dismissScheduledMaintenanceNotification",
					)}
					className="inline-flex size-8 cursor-pointer items-start justify-end border-0 bg-transparent pt-px text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
					onClick={dismiss}
					type="button"
				>
					<Icon aria-hidden="true" name="close" size={20} />
				</button>
			</div>
		</section>
	)
}
